
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChatbotData } from '@/components/chat/chatTypes';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import PersonaForm from '@/components/admin/PersonaForm';
import PersonaList from '@/components/admin/PersonaList';
import SystemPrompts from '@/components/admin/SystemPrompts';
import ApiSettings from '@/components/admin/ApiSettings';

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [chatbots, setChatbots] = useState<ChatbotData[]>([]);
  const [systemPrompts, setSystemPrompts] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if user is admin
  const [isAdmin, setIsAdmin] = useState(true); // In a real app, this would be a proper auth check

  // Fetch agents and system prompts from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      try {
        // Fetch agents
        const { data: agentsData, error: agentsError } = await supabase
          .from('agents')
          .select('*');
        
        if (agentsError) {
          throw agentsError;
        }
        
        // Fetch system prompts
        const { data: promptsData, error: promptsError } = await supabase
          .from('system_prompts')
          .select('*');
        
        if (promptsError) {
          throw promptsError;
        }
        
        // Format data
        const formattedChatbots = agentsData.map((agent: any) => ({
          id: agent.id,
          name: agent.name,
          description: agent.description,
          tags: agent.tags,
          avatarUrl: agent.avatar_url,
          conversations: agent.conversations
        }));
        
        // Create a map of agent_id to prompt content
        const promptsMap: Record<string, string> = {};
        promptsData.forEach((prompt: any) => {
          promptsMap[prompt.agent_id] = prompt.content;
        });
        
        setChatbots(formattedChatbots);
        setSystemPrompts(promptsMap);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load agents and prompts',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [toast]);
  
  // Handle adding a new persona
  const handlePersonaAdded = (newChatbot: ChatbotData) => {
    setChatbots([...chatbots, newChatbot]);
    setSystemPrompts({
      ...systemPrompts,
      [newChatbot.id]: `Default system prompt for ${newChatbot.name}`
    });
  };
  
  // Handle deleting a persona
  const handlePersonaDeleted = (id: string) => {
    setChatbots(chatbots.filter(chatbot => chatbot.id !== id));
    
    // Also remove its system prompt from local state
    const updatedPrompts = { ...systemPrompts };
    delete updatedPrompts[id];
    setSystemPrompts(updatedPrompts);
  };
  
  // Handle updating a system prompt
  const handlePromptUpdated = (chatbotId: string, content: string) => {
    setSystemPrompts({
      ...systemPrompts,
      [chatbotId]: content
    });
  };

  // Convert system prompts object to array for display
  const systemPromptsArray = Object.entries(systemPrompts).map(([chatbotId, content]) => {
    const chatbot = chatbots.find(bot => bot.id === chatbotId);
    return {
      id: chatbotId,
      chatbotId,
      chatbotName: chatbot?.name || 'Unknown',
      content
    };
  });
  
  // Ensure user is admin
  if (!isAdmin) {
    navigate('/dashboard');
    return null;
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage AI personas and system prompts
          </p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <Tabs defaultValue="personas" className="space-y-8">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="personas">Personas</TabsTrigger>
              <TabsTrigger value="prompts">System Prompts</TabsTrigger>
            </TabsList>
            
            {/* Personas Tab */}
            <TabsContent value="personas" className="space-y-6">
              <PersonaForm onPersonaAdded={handlePersonaAdded} />
              <PersonaList chatbots={chatbots} onPersonaDeleted={handlePersonaDeleted} />
            </TabsContent>
            
            {/* System Prompts Tab */}
            <TabsContent value="prompts" className="space-y-6">
              <SystemPrompts 
                systemPrompts={systemPromptsArray} 
                onPromptUpdated={handlePromptUpdated} 
              />
              <ApiSettings />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
};

export default Admin;
